import { inject, Injectable } from '@angular/core';
import { Route, Routes } from '@angular/router';
import { AnfrageGuard } from '../guards/anfrage/anfrage.guard';
import { AnfrageDetailComponent } from '../features/anfrage/forms/anfrage-detail/anfrage-detail.component';
import { GetAnfrageByIdResolver } from '../resolver/anfrage/get-anfrage-by-id.resolver';
import { GetAnfrageValidationErrorsResolver } from '../resolver/anfrage/get-anfrage-validationerrors.resolver';
import { OrganisationFormProcessStepComponent } from '../features/anfrage/forms/organisation-form-process-step/organisation-form-process-step.component';
import { GetAnfrageContactDataResolver } from '../resolver/anfrage/get-anfrage-contact-data.resolver';
import { ReportsGuard } from '../guards/reports/reports.guard';
import { ApplicantFormProcessStepComponent } from '../features/anfrage/forms/applicant-form-process-step/applicant-form-process-step.component';
import { ProjectDataProcessStepComponent } from '../features/anfrage/forms/project-data-process-step/project-data-process-step.component';
import { GetAnfrageProjectDataResolver } from '../resolver/anfrage/get-anfrage-project-data.resolver';
import { FragenProcessStepComponent } from '../features/anfrage/forms/fragen-process-step/fragen-process-step.component';
import { FragenResolver } from '../resolver/fragen/fragen.resolver';
import { FinanceDataProcessStepComponent } from '../features/anfrage/forms/finance-data-process-step/finance-data-process-step.component';
import { GetAnfrageFinanceDataResolver } from '../resolver/anfrage/get-anfrage-finance-data.resolver';
import { GetAllAnfragenDocumenttypesResolver } from '../resolver/anfrage/get-all-anfrage-documenttypes.resolver';
import { AttachmentsProcessStepComponent } from '../features/anfrage/forms/attachments-process-step/attachments-process-step.component';
import { GetAllAnfragenAttachmentsResolver } from '../resolver/anfrage/get-all-anfrage-attachments.resolver';
import { MilestonesProcessStepComponent } from '../features/anfrage/forms/milestones-process-step/milestones-process-step.component';
import { AccessableInDraftGuard } from '../guards/anfrage/accessable-in-draft.guard';
import { StipendiumProccessStepComponent } from '../features/anfrage/forms/stipendium-proccess-step/stipendium-proccess-step.component';
import { GetAnfrageStipendiumResolver } from '../resolver/anfrage/get-anfrage-stipendium.resolver';
import { BaseRoutingFactory } from './base-routing.factory';
import { AnfrageOverviewComponent } from '../features/anfrage/components/anfrage-overview/anfrage-overview.component';
import { GetAllProjektProjektideeAnfragenResolver } from '../resolver/anfrage/get-all-projekt-projektidee-anfragen.resolver';
import { DataprotectionProcessStepComponent } from '../forms/dataprotection-process-step/dataprotection-process-step.component';
import { UserModelResolver } from '../resolver/user/user-model.resolver';
import { GetAnfrageMilestonesDataResolver } from '../resolver/anfrage/get-anfrage-milestones.resolver';
import { OrganisationEligibleFormProcessStepComponent } from '../features/anfrage/forms/organisation-eligible-form-process-step/organisation-eligible-form-process-step.component';
import { MitantragstellerProcessStepComponent } from '../features/anfrage/forms/mitantragsteller-process-step/mitantragsteller-process-step.component';
import { GetMitantragstellerResolver } from '../resolver/anfrage/get-mitantragsteller.resolver';
import { MitantragstellerDataComponent } from '../features/anfrage/forms/mitantragsteller-process-step/mitantragsteller-data/mitantragsteller-data.component';
import { GetMitantragstellerDataResolver } from '../resolver/anfrage/get-mitantragsteller-data.resolver';
import { CanDeactivateGuard } from '../guards/can-deactivate.guard';
import { UebersichtDataComponent } from '../forms/uebersicht-data/uebersicht-data.component';
import { GetAnfrageuebersichtDataResolver } from '../resolver/anfrage/get-anfrageuebersicht-data.resolver';
import { I18nService } from '../services/i18nService';
import { StammdatenService } from '../services/stammdaten.service';
import { InstitutionFormProcessStepComponent } from '../features/anfrage/forms/institution-form-process-step/institution-form-process-step.component';
import { AnsprechpartnerFormProcessStepComponent } from '../features/anfrage/forms/ansprechperson-kontakt-form-process-step/ansprechperson-form-process-step.component';
import { VertretungsberechtigteFormProcessStepComponent } from '../features/anfrage/forms/vertretungsberechtigte-form-process-step/vertretungsberechtigte-form-process-step.component';
import { InformationenProcessStepComponent } from '../features/anfrage/forms/informationen-process-step/informationen-process-step.component';
import { KooperationspartnerProcessStepComponent } from '../features/anfrage/forms/kooperationspartner-process-step/kooperationspartner-process-step.component';
import { GetKooperationspartnerResolver } from '../resolver/anfrage/get-kooperationspartner.resolver';

export class AnfrageRoutingFactory extends BaseRoutingFactory {

    protected stammdatenService = inject(StammdatenService);
    protected languageService = inject(I18nService);

    private readonly coreAnfrageRoute: Routes = [
        {
            path: '',
            redirectTo: 'overview',
            pathMatch: 'full',
        },
        {
            path: 'overview',
            component: this.componentService.get(AnfrageOverviewComponent),
            resolve: {
                anfragen: this.componentService.get(GetAllProjektProjektideeAnfragenResolver),
            },
            title: () => this.getMyApplicationsText + " | " + this.translateService.instant("xfound.portal.tabname")
        },
        {
            path: 'detail/:scope/:id',
            component: this.componentService.get(AnfrageDetailComponent),
            data: { useAnfrageLanguage: true },
            canActivate: [
                this.componentService.get(AnfrageGuard),
            ],
            resolve: {
                anfrage: this.componentService.get(GetAnfrageByIdResolver),
            },
            children: [
                {
                    path: '',
                    redirectTo: 'contact-data',
                    pathMatch: 'full',
                },
                {
                    path: 'contact-data/:abschnittid',
                    component: this.componentService.get(OrganisationFormProcessStepComponent),
                    canDeactivate: [],
                    resolve: {
                        contactData: this.componentService.get(GetAnfrageContactDataResolver),
                    },
                },
                {
                    path: 'institution-form/:abschnittid',
                    component: this.componentService.get(InstitutionFormProcessStepComponent),
                    canDeactivate: [],
                    resolve: {
                        contactData: this.componentService.get(GetAnfrageContactDataResolver),
                    },
                },
                {
                    path: 'applicant-form/:abschnittid',
                    component: this.componentService.get(ApplicantFormProcessStepComponent),
                    canDeactivate: [],
                    resolve: {
                        contactData: this.componentService.get(GetAnfrageContactDataResolver),
                    },
                },
                {
                    path: 'ansprechperson-form/:abschnittid',
                    component: this.componentService.get(AnsprechpartnerFormProcessStepComponent),
                    canDeactivate: [],
                    resolve: {
                        contactData: this.componentService.get(GetAnfrageContactDataResolver),
                    },
                },
                                {
                    path: 'vertretungsberechtigte-form/:abschnittid',
                    component: this.componentService.get(VertretungsberechtigteFormProcessStepComponent),
                    canDeactivate: [],
                    resolve: {
                        contactData: this.componentService.get(GetAnfrageContactDataResolver),
                    },
                },
                {
                    path: 'project-data/:abschnittid',
                    component: this.componentService.get(ProjectDataProcessStepComponent),
                    canDeactivate: [],
                    resolve: {
                        projectData: this.componentService.get(GetAnfrageProjectDataResolver),
                    },
                },
                {
                    path: 'questions/:abschnittid',
                    component: this.componentService.get(FragenProcessStepComponent),
                    resolve: {
                        fragen: this.componentService.get(FragenResolver),
                    },
                    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
                },
                {
                    path: 'information/:abschnittid',
                    component: this.componentService.get(InformationenProcessStepComponent),
                },
                {
                    path: 'finance-data/:abschnittid',
                    component: this.componentService.get(FinanceDataProcessStepComponent),
                    canDeactivate: [],
                    resolve: {
                        financeData: this.componentService.get(GetAnfrageFinanceDataResolver),
                        documenttypes: this.componentService.get(GetAllAnfragenDocumenttypesResolver),
                    },
                },
                {
                    path: 'attachments/:abschnittid',
                    component: this.componentService.get(AttachmentsProcessStepComponent),
                    resolve: {
                        contactData: this.componentService.get(GetAnfrageContactDataResolver),
                        documenttypes: this.componentService.get(GetAllAnfragenDocumenttypesResolver),
                        attachments: this.componentService.get(GetAllAnfragenAttachmentsResolver),
                        userModel: this.componentService.get(UserModelResolver)
                    },
                    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
                },
                {
                    path: 'milestones/:abschnittid',
                    component: this.componentService.get(MilestonesProcessStepComponent),
                    canDeactivate: [CanDeactivateGuard],
                    resolve: {
                        milestones: this.componentService.get(GetAnfrageMilestonesDataResolver),
                    },
                },
                {
                    path: 'stipendium/:abschnittid',
                    component: this.componentService.get(StipendiumProccessStepComponent),
                    resolve: {
                        stipendium: this.componentService.get(GetAnfrageStipendiumResolver),
                    },
                },
                {
                    path: 'dataprotection',
                    component: this.componentService.get(DataprotectionProcessStepComponent),
                    canActivate: [this.componentService.get(AccessableInDraftGuard)],
                    resolve: {
                        errors: this.componentService.get(GetAnfrageValidationErrorsResolver),
                    },
                },
                {
                    path: 'org-eligible/:abschnittid',
                    component: this.componentService.get(OrganisationEligibleFormProcessStepComponent),
                    canDeactivate: [],
                    resolve: {
                        contactData: this.componentService.get(GetAnfrageContactDataResolver),
                    },
                },
                {
                    path: 'mitantragsteller/:abschnittid',
                    component: this.componentService.get(MitantragstellerProcessStepComponent),
                    canDeactivate: [CanDeactivateGuard],
                    resolve: {
                        mitantragsteller: this.componentService.get(GetMitantragstellerResolver),
                    },
                },
                {
                    path: 'mitantragsteller-data/:abschnittid',
                    component: this.componentService.get(MitantragstellerDataComponent),
                    canDeactivate: [],
                    resolve: {
                        mitantragstellerData: this.componentService.get(GetMitantragstellerDataResolver),
                    },
                    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
                },
                {
                    path: 'uebersicht-data/:abschnittid',
                    component: this.componentService.get(UebersichtDataComponent),
                    resolve: {
                        uebersicht: this.componentService.get(GetAnfrageuebersichtDataResolver),
                    },
                },
                {
                    path: 'kooperationspartner/:abschnittid',
                    component: this.componentService.get(KooperationspartnerProcessStepComponent),
                    canDeactivate: [CanDeactivateGuard],
                    resolve: {
                        kooperationspartner: this.componentService.get(GetKooperationspartnerResolver),
                    },
                }
            ],
        },
        {
            path: 'funds',
            loadChildren: () => this.componentService.getAsync(import('@/core/features/funds/funds.module').then((m) => m.FundsModule)),
            canActivate: [
                // FundsGuard
            ],
        },
        {
            path: 'reports',
            loadChildren: () => this.componentService.getAsync(import('@/core/features/reports/reports.module').then((m) => m.ReportsModule)),
            canActivate: [
                this.componentService.get(ReportsGuard)
            ],
        },
        {
            path: 'aenderungsantrag',
            loadChildren: () => this.componentService.getAsync(import('@/core/features/aenderungsantrag/aenderungsantrag.module').then((m) => m.AenderungsantragModule)),
            canActivate: [
            ],
        }
    ];

    public create(): Routes {
        const coreRoute = this.coreRoute;

        // How to add a custom form:
        // Extend from this factory and provide it in the app module
        // Override the create method and call the registerCustomForm method
        // For example:

        // this.registerChild(coreRoute, {
        //     path: 'customized-project-data/:abschnittid',
        //     component: CustomizedProjectDataProcessStepComponent,
        //     canDeactivate: [],
        //     resolve: {
        //         projectData: GetAnfrageProjectDataResolver,
        //     },
        // });

        return coreRoute;
    }

    protected get coreRoute(): Routes {
        return this.coreAnfrageRoute;
    }

    protected registerChild(coreRoute: Routes, route: Route): void {
        const formRoutes = coreRoute![2].children!;
        const foundIndex = formRoutes?.findIndex(
            (formRoute) => formRoute.path === route.path
        );
        if (foundIndex >= 0) {
            formRoutes[foundIndex] = route;
        } else {
            coreRoute![2].children!.push(route);
        }
    }

    public get getMyApplicationsText(): string {
        let overridetext = this.languageService.getCurrentLocalizedTextDisplayModel(this.stammdatenService.Portalkonfiguration.seiteAntraegeBezeichnungTexte ?? [])
        if (overridetext && overridetext.text && overridetext.text.length > 0) {
            return overridetext.text;
        }
        return this.languageService.instant("navigation.my_applications");
    }
}
